/**
 * WebSocketContext.tsx
 * ====================
 * Shared WebSocket context that maintains a single connection with channel multiplexing.
 * Multiple hooks can subscribe to different channels through this shared connection.
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { getWebSocketUrl } from '../utils/api';

// Types
export type WebSocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export type WebSocketMessage = {
  type: string;
  data: any;
  channel?: string;
};

export type SubscriptionAction = {
  action: 'subscribe' | 'unsubscribe';
  channels: string[];
};

type MessageHandler = (message: WebSocketMessage) => void;

interface Subscription {
  id: string;
  channels: string[];
  handler: MessageHandler;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

interface WebSocketContextValue {
  status: WebSocketStatus;
  subscribe: (
    channels: string[],
    handler: MessageHandler,
    options?: { onConnect?: () => void; onDisconnect?: () => void }
  ) => string;
  unsubscribe: (subscriptionId: string) => void;
  sendMessage: (message: WebSocketMessage | SubscriptionAction) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

// Generate unique subscription IDs
let subscriptionCounter = 0;
function generateSubscriptionId(): string {
  return `sub_${++subscriptionCounter}_${Date.now()}`;
}

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');

  // Track all subscriptions
  const subscriptionsRef = useRef<Map<string, Subscription>>(new Map());

  // Track channel reference counts
  const channelCountsRef = useRef<Map<string, number>>(new Map());

  // Message queue for when not connected
  const messageQueueRef = useRef<string[]>([]);

  // Pending channel subscriptions (for when subscribe is called before connection is open)
  const pendingChannelSubscriptionsRef = useRef<Set<string>>(new Set());

  // Reconnection state
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const shouldReconnectRef = useRef(true);
  const maxReconnectAttempts = 20;
  const baseReconnectInterval = 2000;

  // Get all currently subscribed channels
  const getAllChannels = useCallback((): string[] => {
    return Array.from(channelCountsRef.current.keys());
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Clear any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setStatus('connecting');

    // Build URL with existing channels if any
    const channels = getAllChannels();
    let wsUrl = getWebSocketUrl('/websocket/ws/v2');
    if (channels.length > 0) {
      const url = new URL(wsUrl, window.location.origin);
      url.searchParams.set('channels', channels.join(','));
      url.protocol = url.protocol.replace('http', 'ws');
      wsUrl = url.toString();
    }

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[WebSocketContext] Connected');
        setStatus('connected');
        reconnectAttemptsRef.current = 0;

        // Send pending channel subscriptions that were queued while connecting
        if (pendingChannelSubscriptionsRef.current.size > 0) {
          const pendingChannels = Array.from(pendingChannelSubscriptionsRef.current);
          console.log('[WebSocketContext] Sending pending channel subscriptions:', pendingChannels);
          ws.send(JSON.stringify({ action: 'subscribe', channels: pendingChannels }));
          pendingChannelSubscriptionsRef.current.clear();
        }

        // Send queued messages
        while (messageQueueRef.current.length > 0) {
          const message = messageQueueRef.current.shift();
          if (message && ws.readyState === WebSocket.OPEN) {
            ws.send(message);
          }
        }

        // Notify all subscribers
        subscriptionsRef.current.forEach((sub) => {
          sub.onConnect?.();
        });
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          // Handle subscription updates internally (don't dispatch to handlers)
          if (message.type === 'subscription_update') {
            return;
          }

          // Dispatch to all handlers that are subscribed to this message's channel
          // or to handlers with no specific channel filter
          subscriptionsRef.current.forEach((sub) => {
            // If message has a channel, only dispatch to subscribers of that channel
            // If message has no channel, dispatch to all subscribers
            if (!message.channel || sub.channels.length === 0 || sub.channels.includes(message.channel)) {
              try {
                sub.handler(message);
              } catch (err) {
                console.error('[WebSocketContext] Handler error:', err);
              }
            }
          });
        } catch (error) {
          console.error('[WebSocketContext] Failed to parse message:', error);
        }
      };

      ws.onerror = (event) => {
        console.error('[WebSocketContext] Error:', event);
        setStatus('error');
      };

      ws.onclose = () => {
        console.log('[WebSocketContext] Disconnected');
        setStatus('disconnected');
        wsRef.current = null;

        // Notify all subscribers
        subscriptionsRef.current.forEach((sub) => {
          sub.onDisconnect?.();
        });

        // Attempt to reconnect
        if (shouldReconnectRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const backoffTime = Math.min(
            baseReconnectInterval * Math.pow(1.5, reconnectAttemptsRef.current),
            30000
          );
          console.log(
            `[WebSocketContext] Reconnecting in ${backoffTime}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`
          );

          reconnectTimeoutRef.current = window.setTimeout(() => {
            reconnectAttemptsRef.current += 1;
            connect();
          }, backoffTime);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[WebSocketContext] Connection failed:', error);
      setStatus('error');
    }
  }, [getAllChannels]);

  // Send a message (queue if not connected)
  const sendMessage = useCallback((message: WebSocketMessage | SubscriptionAction) => {
    const messageStr = JSON.stringify(message);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(messageStr);
    } else {
      messageQueueRef.current.push(messageStr);
    }
  }, []);

  // Subscribe to channels
  const subscribe = useCallback(
    (
      channels: string[],
      handler: MessageHandler,
      options?: { onConnect?: () => void; onDisconnect?: () => void }
    ): string => {
      const subscriptionId = generateSubscriptionId();

      // Store the subscription
      subscriptionsRef.current.set(subscriptionId, {
        id: subscriptionId,
        channels,
        handler,
        onConnect: options?.onConnect,
        onDisconnect: options?.onDisconnect,
      });

      // Update channel reference counts and subscribe to new channels
      const newChannels: string[] = [];
      channels.forEach((channel) => {
        const currentCount = channelCountsRef.current.get(channel) || 0;
        channelCountsRef.current.set(channel, currentCount + 1);
        if (currentCount === 0) {
          newChannels.push(channel);
        }
      });

      // Subscribe to new channels if connected, otherwise queue them
      if (newChannels.length > 0) {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          sendMessage({ action: 'subscribe', channels: newChannels });
        } else {
          // Queue for when connection opens
          newChannels.forEach(ch => pendingChannelSubscriptionsRef.current.add(ch));
          console.log('[WebSocketContext] Queued pending channel subscriptions:', newChannels);
        }
      }

      // Call onConnect if already connected
      if (status === 'connected') {
        options?.onConnect?.();
      }

      return subscriptionId;
    },
    [sendMessage, status]
  );

  // Unsubscribe
  const unsubscribe = useCallback(
    (subscriptionId: string) => {
      const subscription = subscriptionsRef.current.get(subscriptionId);
      if (!subscription) return;

      // Remove the subscription
      subscriptionsRef.current.delete(subscriptionId);

      // Update channel reference counts and unsubscribe from unused channels
      const channelsToRemove: string[] = [];
      subscription.channels.forEach((channel) => {
        const currentCount = channelCountsRef.current.get(channel) || 0;
        if (currentCount <= 1) {
          channelCountsRef.current.delete(channel);
          channelsToRemove.push(channel);
        } else {
          channelCountsRef.current.set(channel, currentCount - 1);
        }
      });

      // Unsubscribe from channels no longer needed
      if (channelsToRemove.length > 0) {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          sendMessage({ action: 'unsubscribe', channels: channelsToRemove });
        } else {
          // Remove from pending subscriptions if not yet connected
          channelsToRemove.forEach(ch => pendingChannelSubscriptionsRef.current.delete(ch));
        }
      }
    },
    [sendMessage]
  );

  // Connect on mount
  useEffect(() => {
    shouldReconnectRef.current = true;
    connect();

    return () => {
      console.log('[WebSocketContext] Cleanup: Disconnecting');
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const value: WebSocketContextValue = {
    status,
    subscribe,
    unsubscribe,
    sendMessage,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

/**
 * Hook to access the WebSocket context.
 * Throws if used outside of WebSocketProvider.
 */
export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
}

/**
 * Hook to optionally access the WebSocket context.
 * Returns null if used outside of WebSocketProvider (useful for OBS pages).
 */
export function useOptionalWebSocketContext(): WebSocketContextValue | null {
  return useContext(WebSocketContext);
}
